import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { initializeCookies } from "./Utils/CookieHandler";
import apiService from "./Services/APIService";
import AuthCheck from "./Components/Auth/AuthCheck";
import Main from "./Components/Layout/Main";
import Network from "./Modules/Connections/Connections";
import Setting from "./Modules/Settings/Setting";
import Threads from "./Modules/Threads/Threads";
import Tasks from "./Modules/Tasks/Tasks";
import Dashboard from "./Modules/Dashboard/Dashboard";
import InqDashboard from "./Modules/Dashboard/InqDashboard";
import Community from "./Modules/Community/Community";
import Growth from "./Modules/Growth/Growth";
import useIsMobile from "./Hooks/useIsMobile";
import "./Hooks/useHaptics";
import { SyncProvider } from "./Components/Common/SyncProvider";
import { SubscriptionModalProvider } from "./Hooks/useSubscriptionModalState";
import { RecordEntryModal } from "./Utils/RecordEntryModal";
import "./Style/Global.css";
import "flag-icons/css/flag-icons.min.css";
import { syncLogic } from "./Hooks/useSyncLogic";
import CorrectExcel from "./Modules/Connections/CorrectExcel";
import SahiLoader from "./Components/Common/SahiLoader";
import Records from "./Modules/Records/Records";
import ConnectionEntryDialog from "./Modules/Connections/ConnectionEntry";
import ConnectionDetails from "./Modules/Connections/ConnectionDetails";
import ThreadEntry from "./Modules/Threads/ThreadEntry";
import Reports from "./Modules/Reports/Reports";
import CorrectExcelThreads from "./Modules/Threads/CorrectExcelThreads";
import Transactions from "./Modules/Transactions/Transactions";
import Contracts from "./Modules/Contracts/Contracts";
import CorrectExcelTransactions from "./Modules/Connections/CorrectExcelTransactions";
import Feedback from "./Connect/Feedback";
import ThreadsReport from "./Modules/Threads/ThreadsReport";
import Users from "./Modules/Users/Users";
import { isAgencyAllowed, userIsAdmin, userIsCreator, checkIfInqOnlyMode, getAgencyDetails, isSmartSellerMode } from "./Store/Reducers";
import ProtectedRoute from "./ProtectedRoute";
import MobileGrowthRedirect from "./Components/Common/MobileGrowthRedirect";
import KanbanThreads from "./Modules/Threads/KanbanThreads";
import CommitmentView from "./Modules/Threads/CommitmentView";
import Sentiment from "./Connect/Sentiment";
import CustomerConnect from "./Connect/CustomerConnect";
import Connect2 from "./Connect/Connect2";
import OrderStatus from "./Connect/OrderStatus";
import IndexPage from "./Modules/Landing/IndexPage";
import UsageMIS from "./Modules/Reports/UsageMIS";
import LoginPopup from "./Services/LoginPopup";
import OAuthCallback from "./Services/OAuthCallback";
import WelcomeFirstInquiry from "./Components/Congrats/WelcomeFirstInquiry";
import Expenses from "./Modules/Expenses/Expenses";
import { checkUserRights, isMobileBrowser } from "./Utils/Utilities";
import EffortsLog from "./Modules/Users/EffortsLog";
import ThrLossAnalysis from "./Modules/Reports/ThrLossAnalysis";
import Download from "./Modules/Landing/Download";
import Start from "./Modules/Landing/Start";
import ProductDemandReport from "./Modules/Reports/ProductDemandReport";
import ConnectionDetailsMobile from "./Modules/Connections/ConnectionDetailsMobile";
import MISVitals from "./Modules/Reports/MISVitals";
// import TourGuide from "./Components/TourGuide/TourGuide";
// import { getTourSteps } from "./Components/TourGuide/TourSteps";
// import TourWelcomeModal from "./Components/TourGuide/TourWelcomeModal";
import DownloadAppModal from "./Modules/Landing/DownloadAppModal";
import { getAuthData } from "./Utils/AuthUtils";
// import TestSteps from "./Components/TestSteps/TestSteps";
// import TourGuide from "./Components/TourGuide/TourGuide";
// import { getTourSteps } from "./Components/TourGuide/TourSteps";
// import TourWelcomeModal from "./Components/TourGuide/TourWelcomeModal";
import SegmentAnalysis from "./Modules/Reports/SegmentAnalysis";
import CustomerAnalysis from "./Modules/Reports/CustomerAnalysis";
import ExternalTasks from "./Modules/Tasks/ExternalTasks";
import WebStore from "./Connect/WebStore";
import StoreQRCardView from "./Connect/StoreQRCardView";
import HapticsTest from "./Modules/HapticsTest";
import { canShowImportInquiries, SHOW_EXPENSES, SHOW_LISTING } from "./Utils/FeatureFlags";
import { hasFeatureAccess } from "./Utils/Constants";
import IntegrationsPage from "./Modules/Settings/SubSettings/Integrations";
import DetailedThreadsView from "./Modules/Threads/DetailedThreadsView";
import AgencyAlertGate from "./Components/Common/AgencyAlertGate";
import Onboarding from "./Modules/Onboarding/Onboarding";
import SegmentOnboarding from "./Modules/Onboarding/SegmentOnboarding";
import SegmentLanding from "./Modules/Landing/SegmentLanding";
import Listing from "./Modules/Listing/Listing";

function WebStoreRedirect() {
	const location = useLocation();
	return <Navigate to={`/store${location.search}`} replace />;
}

function StoreQRRedirect() {
	const location = useLocation();
	return <Navigate to={`/qr${location.search}`} replace />;
}

// Loading component
function LoadingScreen({ message = "Loading app, please wait...", showProgress = false, progress = "" }) {
	return (
		<div style={{
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			height: '100vh',
			flexDirection: 'column',
			fontFamily: 'Arial, sans-serif',
			position: 'relative'
		}}>
			<SahiLoader />
			{/* <div style={{ marginTop: '100px', textAlign: 'center', zIndex: 1 }}>
				<p>{message}</p>
				{showProgress && (
					<p style={{ color: '#666', fontSize: '14px', marginTop: '10px' }}>
						{progress}
					</p>
				)}
			</div> */}
		</div>
	);
}

// Inner App component that contains the routes and dispatch setup
function InnerApp({ isLoading, setIsLoading }) {
	const isMobile = useIsMobile();
	const isGlobalLoading = useSelector((state) => state.globalLoader.isGlobalLoading);
	const navigate = useNavigate();
	const isAdmin = useSelector(userIsAdmin);
	const isAgencyAuthorized = useSelector(isAgencyAllowed);
	const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
	const defaultThrTypeID = 1; // Default to Inquiry
	const userRights = checkUserRights();
	const [isConnectionDialogOpen, setIsConnectionDialogOpen] = useState(false);
	const [isThreadEntryOpen, setIsThreadEntryOpen] = useState(false);
	const [selectedConnection, setSelectedConnection] = useState(null);
	const [isDetailOpen, setIsDetailOpen] = useState(false);
	const [isFirstInquiryEntry, setIsFirstInquiryEntry] = useState(false);
	const agencyData = useSelector(getAgencyDetails);
	const { data: activeData } = useSelector(state => state.activeData);
	const [openWelcomePage, setOpenWelcomePage] = useState(false);
	const { data: connData } = useSelector(state => state.connectionData);
	// const [showTestSteps, setShowTestSteps] = useState(true);
	// const [showDownloadModal, setShowDownloadModal] = useState(false);
	// const redirectToIndex = agencyData?.SubTypeID === 1 && !agencyData?.SubTillDT && !agencyData?.FirstUpgdDT && !agencyData?.LastUpgdDT;
	const isExpired = agencyData?.SubTillDT && new Date(agencyData.SubTillDT) < new Date();
	const redirectToIndex = false && (agencyData?.SubTypeID === 1 && isExpired);
	// const redirectToIndex = (agencyData?.SubTypeID === 0) || (agencyData?.SubTypeID === 1 && isExpired);

	const connTypeInfo = { singular: "Client" }; // Example, replace with actual

	const handleDetailClose = () => setIsDetailOpen(false);
	const [onboardingSegment, setOnboardingSegment] = useState(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const segFromUrl = urlParams.get("onboarding");
		const segFromStorage = localStorage.getItem("onboarding_segment");
		if (segFromUrl && segFromUrl !== "true" && segFromUrl !== "1") {
			return segFromUrl;
		}
		return segFromStorage || null;
	});

	const isCreator = useSelector(userIsCreator);

	const [isOnboardingOpen, setIsOnboardingOpen] = useState(() => {
		const onboardingCompleted = localStorage.getItem("onboarding_completed");
		const needsOnboarding = localStorage.getItem("needs_onboarding");
		const urlParams = new URLSearchParams(window.location.search);
		const hasUrlParam = urlParams.get("onboarding") != null;
		return (hasUrlParam || (needsOnboarding === "1")) && onboardingCompleted !== "1";
	});

	useEffect(() => {
		const onboardingCompleted = localStorage.getItem("onboarding_completed");
		const needsOnboarding = localStorage.getItem("needs_onboarding");
		const urlParams = new URLSearchParams(window.location.search);
		const hasUrlParam = urlParams.get("onboarding") != null;

		if (onboardingCompleted === "1") {
			setIsOnboardingOpen(false);
		} else if (hasUrlParam) {
			setIsOnboardingOpen(true);
		} else if (needsOnboarding === "1") {
			setIsOnboardingOpen(!!isCreator);
		} else {
			setIsOnboardingOpen(false);
		}
	}, [isCreator, agencyData?.AdminUserID]);

	useEffect(() => {
		if (!isOnboardingOpen) {
			const url = new URL(window.location.href);
			if (url.searchParams.has("onboarding")) {
				url.searchParams.delete("onboarding");
				window.history.replaceState({}, document.title, url.pathname + url.search);
			}
			localStorage.removeItem("onboarding_segment");
		}
	}, [isOnboardingOpen]);
	// const [tourWelcomeModal, setTourWelcomeModal] = useState(false);
	// const [startTour, setStartTour] = useState(false);

	// Add a handler to open inquiry entry from tour
	// const handleEnterInquiryFromTour = () => {
	// 	try {
	// 		setIsThreadEntryOpen(true);
	// 		setIsFirstInquiryEntry(true);
	// 	} catch (error) {
	// 		console.error('Error opening inquiry dialog:', error);
	// 		toast.error("Failed to open inquiry form. Please try again.");
	// 		setIsThreadEntryOpen(true);
	// 	}
	// };
	let signUpData = null;
	useEffect(() => {
		initializeCookies();

		signUpData = JSON.parse(localStorage.getItem('SignUp'));
		const isLoggedIn = localStorage.getItem("isLoggedIn");

		if (Array.isArray(signUpData) && signUpData.length > 0) {
			// User came from fresh signup flow
			// if (signUpData[0].nextAction === "InqEntry" && isInqOnlyMode) {
			// 	setIsThreadEntryOpen(true);
			// 	setIsFirstInquiryEntry(true);
			// 	// Don't remove SignUp flag yet - wait until inquiry is completed
			// 	// setTourWelcomeModal(true);
			// 	// localStorage.removeItem("SignUp");
			// }
		} else {
			// Normal user flow - set logged in flag if not already set
			!isLoggedIn && localStorage.setItem("isLoggedIn", "1");
		}
	}, [activeData?.CntThreads, isInqOnlyMode]);

	useEffect(() => {
		const handleStorageChange = (e) => {
			if (e.key === "Jarvis") {
				window.location.reload();
			}
		};

		window.addEventListener("storage", handleStorageChange);
		return () => {
			window.removeEventListener("storage", handleStorageChange);
		};
	}, []);

	// useEffect(() => {
	// 	const jarvis = getAuthData();
	// 	if (jarvis && jarvis?.IsWeb && isMobileDevice()) {
	// 		setShowDownloadModal(true);
	// 	}
	// }, [isMobile]);

	// Admin route wrapper
	const AdminRoute = ({ children }) => (
		<ProtectedRoute condition={isAdmin}>{children}</ProtectedRoute>
	);

	signUpData = JSON.parse(localStorage.getItem('SignUp'));
	const nextAction = Array.isArray(signUpData) && signUpData.length > 0 ? signUpData[0].nextAction : null;

	return (
		<>
			<SubscriptionModalProvider>
				<SyncProvider navigate={navigate}>
					<AuthCheck redirectUrl="/Web">
						{redirectToIndex ? (
							<Routes>
								<Route path="/" element={<IndexPage />} />
								<Route path="*" element={<Navigate to="/" replace />} />
							</Routes>
						) : (
							<Main onRefresh={() => {
								localStorage.setItem('MyLastSyncDT', '');
								window.location.reload();
							}}>
								<AgencyAlertGate />
								<Routes>
									<Route path="/" element={(isInqOnlyMode || isAgencyAuthorized) ? <InqDashboard /> : <Dashboard />} />
									<Route path="/threads" element={<MobileGrowthRedirect defaultTab="inquiries" desktopElement={<Threads />} />} />
									<Route path="/kanban" element={<KanbanThreads />} />
									<Route path="/threads-detail" element={<DetailedThreadsView />} />
									<Route path="/commitment-view" element={<CommitmentView />} />
									<Route path="/effort-log" element={<EffortsLog />} />
									<Route path="/threads-report" element={<MobileGrowthRedirect defaultTab="sales" desktopElement={<ThreadsReport />} />} />
									<Route path="/records" element={<Records />} />
									<Route path="/tasks" element={<Tasks />} />
									<Route path="/community" element={hasFeatureAccess("community") ? <Community /> : <Navigate to="/directory" replace />} />
									<Route path="/listing" element={SHOW_LISTING() ? <Listing /> : <Navigate to="/community" replace />} />
									<Route path="/directory" element={SHOW_LISTING() ? <Listing /> : <Navigate to="/community" replace />} />
									<Route path="/growth" element={<Growth />} />
									<Route path="/records/:connId" element={<Records />} />
									<Route path="/connections" element={<MobileGrowthRedirect defaultTab="contacts" desktopElement={<Network />} />} />
									<Route path="/correct-excel" element={<CorrectExcel />} />
									<Route path="/correct-excel-threads" element={<CorrectExcelThreads />} />
									<Route path="/correct-excel-transactions" element={<CorrectExcelTransactions />} />
									<Route path="/reports" element={<Reports />} />
									<Route path="/loss-analysis" element={<ThrLossAnalysis />} />
									<Route path="/product-demand" element={<ProductDemandReport />} />
									<Route path="/segment-analysis" element={<SegmentAnalysis />} />
									<Route path="/customer-analysis" element={<CustomerAnalysis />} />

									{/* Protected Modules */}
									<Route
										path="/transactions"
										element={
											<ProtectedRoute condition={agencyData?.EnblXns && userRights?.addTxn}>
												<Transactions />
											</ProtectedRoute>
										}
									/>
									<Route
										path="/contracts"
										element={
											<ProtectedRoute condition={agencyData?.EnblContracts && userRights?.addCtrcs}>
												<Contracts />
											</ProtectedRoute>
										}
									/>

									<Route
										path="/activity"
										element={
											<ProtectedRoute condition={isAgencyAuthorized}>
												<UsageMIS />
											</ProtectedRoute>
										}
									/>

									{/* Admin routes */}
									<Route
										path="/users"
										element={
											<ProtectedRoute condition={isAdmin && !isSmartSellerMode()}>
												<Users />
											</ProtectedRoute>
										}
									/>
									<Route
										path="/settings"
										element={
											<ProtectedRoute condition={isAdmin}>
												<Setting />
											</ProtectedRoute>
										}
									/>
									{/* Expenses routes */}
									<Route
										path="/expenses"
										element={
											<ProtectedRoute condition={(isAdmin || userRights.agnConfig) && SHOW_EXPENSES}>
												<Expenses />
											</ProtectedRoute>
										}
									/>
									<Route
										path="/integrations"
										element={
											<ProtectedRoute condition={isAdmin && canShowImportInquiries()}>
												<IntegrationsPage />
											</ProtectedRoute>
										}
									/>
									<Route path="*" element={<Navigate to="/" replace />} />
								</Routes>

								{isGlobalLoading && <SahiLoader />}

								{/* Dialogs */}
								<ConnectionEntryDialog
									open={isConnectionDialogOpen}
									onOpenChange={setIsConnectionDialogOpen}
									triggerButton={false}
									initialConnType={connTypeInfo.singular}
									onConnectionAdded={() => {
										setIsThreadEntryOpen(false);
										setIsFirstInquiryEntry(false);
										localStorage.removeItem("SignUp");
									}}
								/>

								<ThreadEntry
									open={isThreadEntryOpen && nextAction === "InqEntry"}
									onOpenChange={setIsThreadEntryOpen}
									triggerButton={false}
									threadTypeID={defaultThrTypeID}
									forceOpen={isFirstInquiryEntry && isInqOnlyMode}
									// forceOpen={false}
									onThreadUpdated={() => {
										// Handle thread update if needed
										setIsThreadEntryOpen(false);
										// Reset first inquiry flag and remove SignUp flag after successful completion
										setIsFirstInquiryEntry(false);
										localStorage.removeItem("SignUp");
										if (connData.length == 1) {
											setSelectedConnection({ connID: connData[0].ConnID });
										}
										// setOpenWelcomePage(true);
										// setTimeout(() => {
										// 	setOpenWelcomePage(false);
										// 	setIsDetailOpen(true);
										// }, 4000)
									}}
								/>

								{selectedConnection && (
									!isMobile ? <ConnectionDetails
										isOpen={isDetailOpen}
										onClose={handleDetailClose}
										connID={selectedConnection.connID}
									/> :
										<ConnectionDetailsMobile
											isOpen={isDetailOpen}
											onClose={handleDetailClose}
											connID={selectedConnection.connID}
										/>
								)}

								{/* Tour guide on first signup */}
								{/* <TourGuide
						steps={getTourSteps("INQONLY")?.filter((step) => !isMobile ? step?.element != '[data-tour="inq-signup-integration"]' : true)}
						startTour={startTour}
						allowClose={false}
						onFinish={() => {
							setStartTour(false);
						}}
						onEnterInquiry={handleEnterInquiryFromTour}
						showProgress={true}
						showButtons={true}
						preventNavigation={false}
						autoCloseDelay={null}
					/> */}

								{/* <TourWelcomeModal
						isOpen={tourWelcomeModal}
						onStart={() => {
							setTourWelcomeModal(false);
							setStartTour(true);
						}}
					/> */}

								<WelcomeFirstInquiry
									isOpen={openWelcomePage}
								/>

								{onboardingSegment ? (
									<SegmentOnboarding
										isOpen={isOnboardingOpen}
										setIsOpen={setIsOnboardingOpen}
										segment={onboardingSegment}
									/>
								) : (
									<Onboarding
										isOpen={isOnboardingOpen}
										setIsOpen={setIsOnboardingOpen}
									/>
								)}
								{/* {isInqOnlyMode && (
						<TestSteps open={showTestSteps} onClose={() => setShowTestSteps(false)} hideCloseButton={false} />
					)} */}
							</Main>
						)}
					</AuthCheck>
				</SyncProvider>
			</SubscriptionModalProvider>
			{/* {isMobileDevice() && showDownloadModal && <DownloadAppModal />} */}
			<Toaster
				position={isMobile ? "bottom-center" : "bottom-right"}
				offset={isMobile ? 80 : 32}
			/>
		</>
	);
}



// Outer App component that manages the router and initial sync logic
function App() {
	const [isLoading, setIsLoading] = useState(false);
	const [isInitialized, setIsInitialized] = useState(false);
	const [initError, setInitError] = useState(null);
	const [loadingProgress, setLoadingProgress] = useState("");
	const [isWaitingForData, setIsWaitingForData] = useState(false);
	const dispatch = useDispatch();
	const isMobile = useIsMobile();

	const location = window.location.pathname;

	useEffect(() => {
		if (location === '/' || location === '') {
			window.location.replace('/Web' + window.location.search + window.location.hash);
		}
	}, [location]);

	const isExternalRoute = (pathname) => {
		const path = (pathname || "").toLowerCase();
		const extPrefixes = [
			"/web/feedback", "/feedback",
			"/web/sentiment", "/sentiment",
			"/web/connect", "/connect",
			"/web/login", "/login",
			"/web/callback", "/callback",
			"/web/download", "/download",
			"/web/start", "/start",
			"/web/vitals", "/vitals",
			"/web/ext-tasks", "/ext-tasks",
			"/web/webstore", "/webstore",
			"/web/store", "/store",
			"/web/qr", "/qr",
			"/web/store-qr", "/store-qr",
			"/web/order", "/order",
			"/web/pcb", "/pcb"
		];
		return extPrefixes.some(prefix => path === prefix || path.startsWith(prefix + "/") || path.startsWith(prefix + "?"));
	};

	const isBlockedMobileBrowser = isMobileBrowser() && !isExternalRoute(location);

	useEffect(() => {
		// Skip initialization for external pages or root redirect
		if (location === '/' || location === '' || isExternalRoute(location)) {
			setIsInitialized(true);
			return;
		}
		const initializeApp = async () => {
			try {
				setIsLoading(true);

				// Initialize cookies first
				initializeCookies();

				if (!navigator.onLine) {
					setLoadingProgress("You are offline. Loading app with local data...");
					setIsInitialized(true);
					return; // Exit early for offline mode
				}

				const jarvis = localStorage.getItem('Jarvis');
				if (!jarvis) {
					// User is not logged in: clear any orphaned MyLastSyncDT and finish initialization cleanly
					localStorage.removeItem('MyLastSyncDT');
					setIsInitialized(true);
					return;
				}

				// Call homeResume directly instead of checkAccess
				try {
					const lastSyncDT = localStorage.getItem('MyLastSyncDT');
					if (lastSyncDT != null && jarvis) {
						await apiService.homeResume();
					}
				} catch (homeError) {
					setIsLoading(false);
					setIsInitialized(false);

					window.location.replace("/Web");
					return;
				}

				// Run initial sync only if access check passed
				const sync = syncLogic(setIsLoading, setIsInitialized);
				await new Promise((resolve, reject) => {
					const originalInitialize = sync.initializeSync;
					sync.initializeSync = () => {
						const lastSyncDT = localStorage.getItem('MyLastSyncDT');

						if (lastSyncDT == null) {
							setLoadingProgress("Initializing");
							sync.checkIDBStatus()
								.then(() => {
									setLoadingProgress("Syncing data from the server...");
									return sync.refresh();
								})
								.then(async () => {
									setIsWaitingForData(true);

									// Wait until all data is properly stored and accessible
									await waitForDataToBeReady();

									setLoadingProgress("Your data is ready!");

									// // Small delay before reload to show the message
									setTimeout(() => {
										window.location.reload();
									}, 500);
								})
								.catch(error => {
									console.error('Error during initializeSync:', error);
									setInitError(error);
									setIsInitialized(true);
									resolve();
								});
						} else {
							setLoadingProgress("Syncing data from the server...");
							sync.getIDBData()
								.then(() => {
									setIsInitialized(true);
									resolve();
								})
								.catch(error => {
									console.error('Error fetching IDB data on refresh:', error);
									setIsInitialized(true);
									resolve();
								});
						}
					};

					sync.initializeSync();
				});

			} catch (error) {
				console.error('App initialization error:', error);
				setInitError(error);
				setIsInitialized(true); // Prevent infinite loading
			} finally {
				setIsLoading(false);
			}
		};

		// Function to wait until all required data is available
		const waitForDataToBeReady = async () => {
			return new Promise((resolve) => {
				let attempts = 0;
				const maxAttempts = 300; // 30 seconds with 100ms intervals (safety limit)

				const checkData = async () => {
					attempts++;

					try {
						// Check localStorage flags
						const lastSyncDT = localStorage.getItem('MyLastSyncDT');
						const getIDB = localStorage.getItem('getIDB');
						const jarvisData = localStorage.getItem('Jarvis');

						// Check if basic localStorage data is present
						if (!lastSyncDT || getIDB !== '1') {
							setLoadingProgress(`Waiting for localStorage... (${attempts})`);
							if (attempts < maxAttempts) {
								setTimeout(checkData, 100);
								return;
							} else {
								resolve();
								return;
							}
						}

						// Check IndexedDB data availability
						const idbReady = await checkIndexedDBReady();
						if (!idbReady) {
							setLoadingProgress(`Waiting for IndexedDB... (${attempts})`);
							if (attempts < maxAttempts) {
								setTimeout(checkData, 100);
								return;
							} else {
								resolve();
								return;
							}
						}

						setLoadingProgress("All data ready!");
						resolve();

					} catch (error) {
						console.error('Error during data verification:', error);
						if (attempts < maxAttempts) {
							setTimeout(checkData, 100);
						} else {
							resolve();
						}
					}
				};

				checkData();
			});
		};

		// Function to check if IndexedDB is ready and has data
		const checkIndexedDBReady = () => {
			const requiredStores = ["Conns", "Threads", "Contracts", "Tasks"];
			return new Promise((resolve) => {
				try {
					const request = indexedDB.open('SahiCRMIDB', 1);

					request.onsuccess = () => {
						const db = request.result;



						// Check if any required store is missing
						const missingStores = requiredStores.some(store => !db.objectStoreNames.contains(store));
						if (missingStores) {
							db.close();
							resolve(false);
							return;
						}

						// Check if stores have data - ADD 'Contracts' to transaction
						const transaction = db.transaction(requiredStores, 'readonly');
						const connsStore = transaction.objectStore('Conns');
						const threadsStore = transaction.objectStore('Threads');
						const contractsStore = transaction.objectStore('Contracts'); // ADD this line
						const taskStore = transaction.objectStore('Tasks'); // ADD this line

						let connsCount = 0;
						let threadsCount = 0;
						let contractsCount = 0; // ADD this line
						let checksCompleted = 0;
						let tasksCount = 0;

						const checkComplete = () => {
							checksCompleted++;
							if (checksCompleted === 4) { // CHANGE from 2 to 3
								db.close();
								resolve(true);
							}
						};

						const connsCountRequest = connsStore.count();
						connsCountRequest.onsuccess = () => {
							connsCount = connsCountRequest.result;
							checkComplete();
						};
						connsCountRequest.onerror = () => {
							checkComplete();
						};

						const threadsCountRequest = threadsStore.count();
						threadsCountRequest.onsuccess = () => {
							threadsCount = threadsCountRequest.result;
							checkComplete();
						};
						threadsCountRequest.onerror = () => {
							checkComplete();
						};

						const contractsCountRequest = contractsStore.count();
						contractsCountRequest.onsuccess = () => {
							contractsCount = contractsCountRequest.result;
							checkComplete();
						};
						contractsCountRequest.onerror = () => {
							checkComplete();
						};

						const taskCountRequest = taskStore.count();
						taskCountRequest.onsuccess = () => {
							tasksCount = taskCountRequest.result;
							checkComplete();
						};
						taskCountRequest.onerror = () => {
							checkComplete();
						};
					};

					request.onerror = () => {
						resolve(false);
					};

				} catch (error) {
					console.error('Error checking IndexedDB:', error);
					resolve(false);
				}
			});
		};

		initializeApp();
	}, []);

	useEffect(() => {
		apiService.setDispatchCallback(dispatch);
	}, [dispatch])

	if (location === '/' || location === '') {
		return null;
	}

	// if (isMobileBrowser() && !extLocations.includes(location)) {
	// 	return <DownloadAppModal isMobile={true} />;
	// }

	// Show SahiLoader directly with progress messages
	// if (isBlockedMobileBrowser) {
	// 	return <DownloadAppModal isMobile={true} />;
	// }

	if (!isInitialized || isWaitingForData) {
		return (
			<SahiLoader
			// message={loadingProgress || ""}
			// progress={isWaitingForData ? "Finalizing data setup..." : ""}
			/>
		);
	}

	// Show error state if initialization failed
	if (initError) {
		return (
			<>
				<IndexPage />
				<Toaster position={isMobile ? "bottom-center" : "bottom-right"}
					offset={isMobile ? 80 : 32}
					toastOptions={{ style: { background: "rgb(202 240 197)", textAlign: "center", justifyContent: "center" }, }} />
			</>
		);
	}

	return (
		<Router basename="/Web">
			<Routes>
				{/* <Route path="/" element={<IndexPage />} /> */}
				<Route path="/feedback" element={<Feedback />} />
				<Route path="/sentiment" element={<Sentiment />} />
				<Route path="/connect" element={<CustomerConnect />} />
				<Route path="/login" element={<LoginPopup />} />
				<Route path="/download" element={<Download />} />
				<Route path="/start" element={<Start />} />
				<Route path="/vitals" element={<MISVitals />} />
				<Route path="/callback" element={<OAuthCallback />} />
				<Route path="/ext-tasks" element={<ExternalTasks />} />
				<Route path="/connect2" element={<Connect2 />} />
				<Route path="/order" element={<OrderStatus />} />
				<Route path="/store" element={<WebStore />} />
				<Route path="/webstore" element={<WebStoreRedirect />} />
				<Route path="/qr" element={<StoreQRCardView />} />
				<Route path="/store-qr" element={<StoreQRRedirect />} />
				<Route path="/haptics" element={<HapticsTest />} />
				<Route path="/pcb" element={<SegmentLanding segment="pcb" />} />
				<Route path="/web/pcb" element={<SegmentLanding segment="pcb" />} />
				{/* All authenticated app routes */}
				<Route path="/*" element={<InnerApp isLoading={isLoading} setIsLoading={setIsLoading} />} />
			</Routes>
			{/* Toast container for external pages */}
			<Toaster
				position={isMobile ? "bottom-center" : "bottom-right"}
				offset={isMobile ? 80 : 32}
			/>
		</Router>
	);
}

export default App;